import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import "../../styles/CarouselGallery.css";
import { getAll } from "../../api/API";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { Carousel } from "react-bootstrap";

/** Custom Carousel Subcomponent */
const CustomCarousel = ({ media }) => {
  const [index, setIndex] = useState(0);
  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);

  const handleSelect = (selectedIndex) => setIndex(selectedIndex);
  const handlePrev = () => index > 0 && setIndex(index - 1);
  const handleNext = () => index < media.length - 1 && setIndex(index + 1);

  return (
    <div className="position-relative custom-carousel-wrapper">
      <Carousel
        activeIndex={index}
        onSelect={handleSelect}
        className="h-100"
        indicators={false}
        controls={false} // Disable Bootstrap default controls
        interval={5000}
        pause="hover"
        fade
      >
        {media.map((item, i) => (
          <Carousel.Item key={item.id || i} className="h-100 position-relative">
            <div className="carousel-slide-wrapper">
              <div
                className="carousel-slide-bg"
                style={{ backgroundImage: `url(${item.url})` }}
              ></div>
              <img
                src={item.url}
                alt={item.alt || "Media item"}
                className="carousel-img"
                loading="lazy"
              />
            </div>
          </Carousel.Item>
        ))}
      </Carousel>

      {/* Prev Button (matching HeroCarousel) */}
      {media.length > 1 && (
        <button
          className={`carousel-control-prev btn-left ${
            hoverPrev ? "hovered" : ""
          } ${index === 0 ? "disabled" : ""}`}
          onClick={handlePrev}
          disabled={index === 0}
          aria-label="Previous slide"
          onMouseEnter={() => setHoverPrev(true)}
          onMouseLeave={() => setHoverPrev(false)}
        >
          <div className="icon-circle carousel-btn-icon">
            <BsChevronLeft
              size={32}
              style={{ color: "#2c0446" }}
              aria-hidden="true"
            />
          </div>
        </button>
      )}

      {/* Next Button (matching HeroCarousel) */}
      {media.length > 1 && (
        <button
          className={`carousel-control-next btn-right ${
            hoverNext ? "hovered" : ""
          } ${index === media.length - 1 ? "disabled" : ""}`}
          onClick={handleNext}
          disabled={index === media.length - 1}
          aria-label="Next slide"
          onMouseEnter={() => setHoverNext(true)}
          onMouseLeave={() => setHoverNext(false)}
        >
          <div className="icon-circle carousel-btn-icon">
            <BsChevronRight
              size={32}
              style={{ color: "#2c0446" }}
              aria-hidden="true"
            />
          </div>
        </button>
      )}
    </div>
  );
};

/** Main Component */
const CarouselGallery = () => {
  const [folders, setFolders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [folderMedia, setFolderMedia] = useState({});
  const galleryRefs = useRef([]);

  /** Fetch folder list */
  const fetchFolders = async () => {
    try {
      const data = await getAll("media-folders", { depth: 1 });
      const filteredFolders = data.docs.filter(
        (f) =>
          f.slug !== "generic" &&
          f.slug !== "eventhero" &&
          f.slug !== "abouthero",
      );
      setFolders(filteredFolders);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching folders:", err);
      setLoading(false);
    }
  };

  /** Fetch media per folder */
  const fetchMediaByFolder = async (folderSlug) => {
    try {
      const data = await getAll("media", {
        depth: 2,
        where: {
          "folder.slug": { equals: folderSlug },
          featuredStories: { equals: false },
        },
      });
      return data.docs.filter((m) => m.mimeType.startsWith("image/"));
    } catch (err) {
      console.error("Error fetching media:", err);
      return [];
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    const loadMedia = async () => {
      if (folders.length > 0) {
        const slug = folders[currentIndex].slug;
        if (!folderMedia[slug]) {
          const media = await fetchMediaByFolder(slug);
          setFolderMedia((prev) => ({ ...prev, [slug]: media }));
        }
      }
    };
    loadMedia();
  }, [folders, currentIndex]);

  /** Scroll active section into view */
  useEffect(() => {
    if (galleryRefs.current[currentIndex]) {
      galleryRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) =>
      folders.length > 0 && prev < folders.length - 1 ? prev + 1 : prev,
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  if (loading) return <LoadingSpinner color="primary" />;
  if (!folders.length)
    return <p className="text-center">No folders available</p>;

  return (
    <div>
      {folders.slice(0, currentIndex + 1).map((folder, idx) => {
        const media = folderMedia[folder.slug] || [];
        const hasStatic = media.length > 2;
        const carouselMedia = hasStatic
          ? media.slice(0, media.length - 2)
          : media;
        const staticMedia = hasStatic ? media.slice(-2) : [];

        return (
          <div
            key={folder.id}
            ref={(el) => (galleryRefs.current[idx] = el)}
            className="container my-5"
          >
            <div className="row">
              {/* Mobile title */}
              <div className="d-block d-md-none mb-3 text-center">
                <motion.div
                  className="fw-bold text-uppercase text-primary"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  {folder.name.toUpperCase()}
                  {folder.subtitle && (
                    <p className="text-muted mb-0">{folder.subtitle}</p>
                  )}
                </motion.div>
              </div>

              {/* Carousel */}
              <div className={hasStatic ? "col-md-6" : "col-md-12"}>
                {carouselMedia.length > 0 && (
                  <div className="border border-5 border-primary shadow position-relative">
                    <CustomCarousel media={carouselMedia} />
                    <div className="carousel-title text-center bg-primary text-white py-2 d-none d-md-block">
                      {folder.name.toUpperCase()}
                    </div>
                  </div>
                )}
              </div>

              {/* Static media on right */}
              {hasStatic && (
                <div className="col-md-6 d-none d-md-flex flex-column gap-4">
                  <motion.div
                    className="fw-bold text-uppercase wise-secondary-gradient mb-2"
                    initial={{ x: "100vw" }}
                    animate={{ x: 0 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  >
                    {folder.name.toUpperCase()}
                    {folder.subtitle && (
                      <p className="text-muted">{folder.subtitle}</p>
                    )}
                  </motion.div>

                  {staticMedia.map((item) => (
                    <img
                      key={item.id}
                      src={item.url}
                      alt={item.alt || "Media preview"}
                      className="static-image"
                      loading="lazy"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Folder navigation buttons (Up/Down) */}
      <div className="text-center my-4 d-flex justify-content-center gap-3">
        <button
          className="btn btn-outline-primary rounded-circle"
          onClick={handlePrev}
          style={{ width: "70px", height: "70px" }}
          disabled={currentIndex === 0}
        >
          <FaArrowUp size={28} />
        </button>
        <button
          className="btn btn-outline-primary rounded-circle"
          onClick={handleNext}
          style={{ width: "70px", height: "70px" }}
          disabled={currentIndex === folders.length - 1}
        >
          <FaArrowDown size={28} />
        </button>
      </div>
    </div>
  );
};

export default CarouselGallery;
